import "./ChatPage.css";

import { Toast } from "../components/Toast";
import { useChat } from "../hooks/use_chat";
import { useAgent } from "../hooks/use_agent";
import { authClient } from "../lib/authClient";
import { renderToolRun } from "../chat/tool_ui";
import { renderHitlRequest } from "../chat/hitl_ui";
import type { ChatInterrupt, ChatMessage, ChatTool } from "../types/chat";
import { useState, useRef, useEffect, useCallback } from "react";
import { useChatMessagesInfinite } from "../hooks/use_chat_messages";

export type TradeAIAgentState = {
  user_name: string;
  language: string;
  agent_enabled: boolean;
  hitl_decision?: { decisions: { type: "approve" | "edit" | "reject" }[] } | null;
};

export default function ChatPage() {
  const sessionQuery = authClient.useSession();

  const welcomeMessage: ChatMessage = {
    role: "assistant",
    content:
      "Olá! Sou seu assistente de trading. Posso ajudá-lo a:\n• Abrir posições LONG/SHORT\n• Consultar preços e análises\n• Gerenciar suas trades\n• Configurar stop loss e take profit\n\nComo posso ajudá-lo hoje?",
  };

  const [inputValue, setInputValue] = useState("");
  const [sendAs] = useState<"user" | "assistant">("user");
  const [openToolDetails, setOpenToolDetails] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<{ message: string; type?: "error" | "success" | "info" } | null>(null);
  const [historyReady, setHistoryReady] = useState(false);

  const historyInitRef = useRef(false);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesInnerRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true);
  // Ligado durante o prepend de histórico antigo: o conteúdo cresce por cima e
  // o scroll é restaurado manualmente, então o stick-to-bottom não pode agir.
  const suppressAutoScrollRef = useRef(false);

  const chatMessagesRef = useRef<ChatMessage[]>([]);
  const chatToolsRef = useRef<ChatTool[]>([]);
  const chatInterruptsRef = useRef<ChatInterrupt[]>([]);

  const agent = useAgent<TradeAIAgentState>({
    debug: true,
    threadId: sessionQuery.data?.user?.id,
    agentId: "/ai/chat",
    state: {
      agent_enabled: true,
      user_name: sessionQuery.data?.user?.name || "",
      // @ts-ignore
      language: sessionQuery.data?.userSettings?.language || "pt-BR",
    },
  });

  const chat = useChat(agent);

  const history = useChatMessagesInfinite({
    userId: sessionQuery.data?.user?.id,
    enabled: !sessionQuery.isPending,
  });

  useEffect(() => {
    chatMessagesRef.current = chat.messages;
  }, [chat.messages]);

  useEffect(() => {
    chatToolsRef.current = chat.tools;
  }, [chat.tools]);

  useEffect(() => {
    chatInterruptsRef.current = chat.interrupts;
  }, [chat.interrupts]);

  const formatTime = (date: string | undefined) => {
    if (!date) {
      return "";
    }

    return new Date(date).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const prevPageCountRef = useRef(0);
  const isChatInputBlocked = !historyReady || chat.isLocked;

  // Bolha em "gerando em segundo plano": o transporte caiu mas o run segue
  // vivo no servidor. Derivado das mensagens para ser reativo — os getters do
  // agent vivem em refs e não disparam render sozinhos.
  const hasPendingRun = chat.messages.some(
    (message) => message.metadata?.kind === "assistant_pending",
  );

  useEffect(() => {
    if (history.error) {
      setToast({ type: "error", message: "Não foi possível carregar o histórico da conversa." });
    }
  }, [history.error]);

  useEffect(() => {
    if (historyInitRef.current) {
      return;
    }

    if (history.isLoading) {
      return;
    }

    if (!sessionQuery.data?.user?.id) {
      return;
    }

    historyInitRef.current = true;

    const first = history.pages[0]?.messages ?? [];
    const firstTools = history.pages[0]?.tools ?? [];
    const firstInterrupts = history.pages[0]?.interrupts ?? [];

    if (first.length === 0 && firstTools.length === 0 && firstInterrupts.length === 0) {
      chat.setMessages([welcomeMessage]);
      chat.setTools([]);
      chat.setInterrupts([]);
      setHistoryReady(true);
      prevPageCountRef.current = history.pages.length;
      return;
    }

    chat.setMessages(first);
    chat.setTools(firstTools);
    chat.setInterrupts(firstInterrupts);
    setHistoryReady(true);
    prevPageCountRef.current = history.pages.length;
  }, [chat, history.isLoading, history.pages, sessionQuery.data?.user?.id]);

  useEffect(() => {
    const nextCount = history.pages.length;
    const prevCount = prevPageCountRef.current;

    // Evita duplicar a primeira página (o load inicial já fez setMessages).
    if (prevCount === 0) {
      prevPageCountRef.current = nextCount;
      return;
    }

    if (nextCount <= prevCount) {
      prevPageCountRef.current = nextCount;
      return;
    }

    // Uma nova página foi adicionada no final (mais antiga). Prepend + preserva scroll.
    const newPage = history.pages[nextCount - 1];
    const newMessages = newPage?.messages ?? [];
    const newTools = newPage?.tools ?? [];
    const newInterrupts = newPage?.interrupts ?? [];

    if (
      newMessages.length === 0 &&
      newTools.length === 0 &&
      newInterrupts.length === 0
    ) {
      prevPageCountRef.current = nextCount;
      return;
    }

    const el = messagesContainerRef.current;
    const prevScrollHeight = el?.scrollHeight ?? 0;
    const prevScrollTop = el?.scrollTop ?? 0;

    const existingIds = new Set(chatMessagesRef.current.map((m) => m.id).filter(Boolean) as string[]);
    const filtered = newMessages.filter((m) => (m.id ? !existingIds.has(m.id) : true));

    const existingToolIds = new Set(chatToolsRef.current.map((t) => t.id).filter(Boolean) as string[]);
    const filteredTools = newTools.filter((t) => (t.id ? !existingToolIds.has(t.id) : true));

    const existingInterruptIds = new Set(chatInterruptsRef.current.map((i) => i.id).filter(Boolean) as string[]);
    const filteredInterrupts = newInterrupts.filter((i) => (i.id ? !existingInterruptIds.has(i.id) : true));

    if (filtered.length > 0 || filteredTools.length > 0 || filteredInterrupts.length > 0) {
      suppressAutoScrollRef.current = true;

      if (filteredTools.length > 0) {
        chat.prependTools(filteredTools);
      }

      if (filteredInterrupts.length > 0) {
        chat.prependInterrupts(filteredInterrupts);
      }

      if (filtered.length > 0) {
        chat.prependMessages(filtered);
      }

      requestAnimationFrame(() => {
        const el2 = messagesContainerRef.current;

        if (!el2) {
          return;
        }

        const nextScrollHeight = el2.scrollHeight;
        const delta = nextScrollHeight - prevScrollHeight;

        el2.scrollTop = prevScrollTop + delta;
      });
    }

    prevPageCountRef.current = nextCount;
  }, [chat, history.pages]);

  const loadOlderHistory = useCallback(async () => {
    if (!history.hasNextPage || history.isFetchingNextPage) {
      return;
    }

    await history.fetchNextPage();
  }, [history]);

  // Stick-to-bottom: qualquer crescimento do conteúdo (item novo na timeline,
  // card de tool recebendo output, tokens em streaming, widget carregando)
  // rola até o fim, desde que o usuário não tenha subido pra ler algo.
  // Observamos o wrapper interno porque o scroller em si não muda de tamanho.
  // O scroll é instantâneo de propósito: smooth dispara eventos intermediários
  // longe do fim e derrubaria o shouldAutoScrollRef no meio da animação.
  useEffect(() => {
    const inner = messagesInnerRef.current;

    if (!inner) {
      return;
    }

    const observer = new ResizeObserver(() => {
      if (suppressAutoScrollRef.current) {
        suppressAutoScrollRef.current = false;
        return;
      }

      if (!shouldAutoScrollRef.current) {
        return;
      }

      const el = messagesContainerRef.current;

      if (!el) {
        return;
      }

      el.scrollTop = el.scrollHeight;
    });

    observer.observe(inner);

    return () => observer.disconnect();
  }, []);

  return (
    <div className="chat-page">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="chat-container">
        <div className="chat-toolbar">
          <button className="toolbar-button">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <div className="toolbar-title">
            <span className="toolbar-title-main">Trade IA</span>
            <span className="toolbar-title-sub">Assistente Inteligente</span>
          </div>
          <button className="toolbar-button">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
              />
            </svg>
          </button>
        </div>

        <div
          className="chat-messages"
          ref={messagesContainerRef}
          onScroll={() => {
            const el = messagesContainerRef.current;

            if (!el) {
              return;
            }

            const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
            shouldAutoScrollRef.current = distanceFromBottom < 120;

            // Scroll infinito para cima: quando estiver perto do topo, carrega mais.
            if (el.scrollTop < 140) {
              void loadOlderHistory();
            }
          }}
        >
          <div className="chat-messages-inner" ref={messagesInnerRef}>
            {history.isLoading && chat.messages.length === 0 ? (
              <div className="chat-history-loading">Carregando histórico…</div>
            ) : null}
            {chat.getTimeline({ order: ["tool", "message", "interrupt"] }).map(item => {
              switch (item.kind) {
                case "tool":
                  return renderToolRun({
                    tool: item.tool,
                    isLocked: chat.isLocked,
                    agentIsRunning: agent.isRunning,
                    isExpanded: !!openToolDetails[item.id],
                    onToggleExpanded: () => {
                    // Expandir/recolher é ação do usuário, não conteúdo novo:
                    // o card muda de altura mas a tela não deve pular pro fim.
                    suppressAutoScrollRef.current = true;
                    setOpenToolDetails((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
                  },
                    sendUserMessage: (content) => chat.sendMessage({ role: "user", content }),
                  })
                case "interrupt": {
                  const interruptStatus = item.metadata?.status;
                  const resolvedStatus =
                    /^approv(e|ed)$/i.test(interruptStatus ?? "")
                      ? "approved"
                      : /^reject(ed)?$/i.test(interruptStatus ?? "")
                        ? "rejected"
                        : /^expir(e|ed)$/i.test(interruptStatus ?? "")
                          ? "expired"
                          : chat.hitlStatus;

                  return renderHitlRequest({
                    status: resolvedStatus,
                    interrupt: item.interrupt,
                    metadata: item.metadata,
                    onDecision: (decision) => chat.sendHitlDecision(decision),
                    onExpired: () => chat.expireInterrupt(item.id),
                  });
                }
                case "message": {
                  const message = item.message;
                  const isUser = message.role === "user";
                  const isAgent = message.role === "assistant";

                  // Anchor messages used only for tool-grouping — no visible content
                  const isInvisibleAnchor =
                    isAgent &&
                    !agent.isRunning &&
                    !(message.content ?? "").trim() &&
                    (message.metadata?.kind === "assistant_waiting_decision" ||
                      message.metadata?.kind === "assistant_tool_only");

                  if (isInvisibleAnchor) {
                    return null;
                  }

                  return (
                    <div
                      key={message.id}
                      className={`message-wrapper ${isUser ? "message-user" : "message-ai"
                        }`}
                    >
                      {isAgent && (
                        <div className="ai-avatar">
                          <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                            />
                          </svg>
                        </div>
                      )}
                      <div
                        className={`chat-bubble chat-bubble-${isUser ? "user" : "assistant"}${
                          message.metadata?.kind === "assistant_error" ? " chat-bubble-error" : ""
                        }`}
                      >
                        {isUser ? (
                          <>
                            <div className="agent-message-header agent-message-header-user">
                              <span className="agent-name agent-name-user">Você</span>
                              {message.created_at ? (
                                <span className="agent-time">{formatTime(message.created_at)}</span>
                              ) : null}
                            </div>
                            <p className="message-text">{message.content}</p>
                          </>
                        ) : (
                          <>
                            {isAgent ? (
                              <div className="agent-message-header">
                                <span className="agent-logo" aria-hidden="true">
                                  IA
                                </span>
                                <span className="agent-name">Trade IA WeDo</span>
                                {message.created_at ? (
                                  <span className="agent-time">{formatTime(message.created_at)}</span>
                                ) : null}
                              </div>
                            ) : null}
                            {message.metadata?.kind === "assistant_pending" ? (
                              <>
                                {message.content && (
                                  <p className="message-text">{message.content}</p>
                                )}
                                <p className="message-pending">
                                  Gerando em segundo plano… a resposta continua
                                  sendo produzida no servidor.
                                </p>
                              </>
                            ) : agent.isRunning ? (
                              message.content ? (
                                <p className="message-text">
                                  {message.content}
                                  <span className="typing-cursor">|</span>
                                </p>
                              ) : (
                                <div className="typing-indicator">
                                  <span className="typing-dot"></span>
                                  <span className="typing-dot"></span>
                                  <span className="typing-dot"></span>
                                </div>
                              )
                            ) : (
                              <>
                                {message.content && (
                                  <p className="message-text">{message.content}</p>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                }
              }
            })}
          </div>
        </div>

        {(agent.isRunning || hasPendingRun) && (
          <div className="run-controls">
            {agent.isRunning && (
              <>
                {/*
                  Cancelamento EXPLÍCITO. Interrompe a geração de verdade — o
                  agente para e nenhuma resposta entra no histórico.
                */}
                <button
                  type="button"
                  className="run-control-button run-control-cancel"
                  onClick={() => void chat.cancelStream()}
                >
                  Cancelar resposta
                </button>

                {/*
                  Só solta o transporte, sem cancelar nada. É o equivalente, no
                  navegador, de o app iOS ir para background: o run sobrevive e
                  a resposta pode ser recuperada depois.
                */}
                <button
                  type="button"
                  className="run-control-button"
                  onClick={() => chat.detachStream()}
                >
                  Simular perda de conexão
                </button>
              </>
            )}

            {hasPendingRun && !agent.isRunning && (
              <button
                type="button"
                className="run-control-button run-control-reattach"
                onClick={() => void chat.reattachStream()}
              >
                Reconectar
              </button>
            )}
          </div>
        )}

        <div className="chat-input-area">
          <div className="chat-input-wrapper">
            <button className="input-button" disabled={isChatInputBlocked}>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                />
              </svg>
            </button>
            <input
              type="text"
              className="chat-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={
                !historyReady
                  ? "Carregando conversa…"
                  : chat.isLocked
                    ? "Aguardando aprovação (HITL)…"
                    : "Digite sua mensagem…"
              }
              disabled={isChatInputBlocked}
              onKeyDown={async (e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();

                  if (isChatInputBlocked) {
                    return;
                  }

                  shouldAutoScrollRef.current = true;
                  const sent = await chat.sendMessage({ role: sendAs, content: inputValue });

                  if (sent) {
                    setInputValue("");
                  }
                }
              }}
            />
            <button
              className="send-button"
              onClick={async () => {
                if (isChatInputBlocked) {
                  return;
                }

                shouldAutoScrollRef.current = true;
                const sent = await chat.sendMessage({ role: sendAs, content: inputValue });

                if (sent) {
                  setInputValue("");
                }
              }}
              disabled={isChatInputBlocked}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                />
              </svg>
            </button>
          </div>
          <p className="input-hint">
            {!historyReady ? "Carregando histórico da conversa…" : 'Use comandos naturais como "Abrir LONG BTC 2x com 25% do saldo"'}
          </p>
        </div>
      </div>
    </div>
  );
}

